import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users, Mail, CheckSquare, TrendingUp, Phone, Linkedin,
  Calendar, ArrowRight, Activity, ToggleRight, Share2,
} from 'lucide-react';
import { pipelineAPI, sequencesAPI, tasksAPI, activitiesAPI, socialAPI } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toast } from 'sonner';
import { useT } from '@/lib/LanguageContext';
import { safeFormat } from '@/lib/utils';
import { getCurrentUser } from '@/lib/auth';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const h = (hex || '#A62B1F').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) || 166;
  const g = parseInt(h.substring(2, 4), 16) || 43;
  const b = parseInt(h.substring(4, 6), 16) || 31;
  return `${r}, ${g}, ${b}`;
}

function getActivityIcon(type) {
  const map = { email: Mail, call: Phone, linkedin: Linkedin, meeting: Calendar, stage_change: TrendingUp };
  return map[type] || Activity;
}

function activityVerb(type, t) {
  const map = {
    email:        'Email',
    call:         'Call',
    linkedin:     'LinkedIn',
    meeting:      'Meeting',
    note:         'Note',
    stage_change: t('dashboard.stage_change'),
  };
  return map[type] || type;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon: Icon, loading, color = '#A62B1F' }) {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-[var(--radius)]" style={{ background: color }} />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-5">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `rgba(${hexToRgb(color)}, 0.12)` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-8 w-20" /> : (
          <div className="text-2xl font-bold tabular-nums text-foreground">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Stage pip bar ────────────────────────────────────────────────────────────

function StagePips({ stages, contactsByStage, total }) {
  if (!total) return null;
  return (
    <div className="space-y-1.5">
      {stages.map((stage) => {
        const count = contactsByStage[stage.id] || 0;
        if (!count) return null;
        const pct = Math.round((count / total) * 100);
        return (
          <div key={stage.id} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: stage.color || '#9AA3AB' }} />
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: stage.color || '#9AA3AB' }} />
            </div>
            <span className="text-xs text-muted-foreground w-4 text-right">{count}</span>
            <span className="text-xs text-muted-foreground w-24 truncate">{stage.name}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Project Card ─────────────────────────────────────────────────────────────

function ProjectCard({ project, contacts, activeSeqs, openTasks, onClick }) {
  const t = useT();
  const color = project.color || '#A62B1F';
  const stages = project.pipeline_stages || [];
  const contactsByStage = {};
  const total = contacts.length;
  for (const c of contacts) {
    const s = c.stage || 'new_lead';
    contactsByStage[s] = (contactsByStage[s] || 0) + 1;
  }
  const won = contactsByStage['won'] || 0;
  const lost = contactsByStage['lost'] || 0;
  const closed = won + lost;
  const winRate = closed ? Math.round((won / closed) * 100) : null;

  return (
    <Card
      className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200 group"
      onClick={onClick}
    >
      <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-[var(--radius)]" style={{ background: color }} />
      <CardHeader className="pt-5 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
            <h3 className="font-semibold text-foreground text-sm leading-tight">{project.name}</h3>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {project.description && (
          <p className="text-xs text-muted-foreground mt-1 ml-5 line-clamp-1">{project.description}</p>
        )}
      </CardHeader>
      <CardContent className="pb-4 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            <span><span className="font-semibold text-foreground">{total}</span> {t('dashboard.contacts')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ToggleRight className="h-3.5 w-3.5" />
            <span><span className="font-semibold text-foreground">{activeSeqs}</span> {t('dashboard.campaigns')}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CheckSquare className="h-3.5 w-3.5" />
            <span><span className="font-semibold text-foreground">{openTasks}</span> {t('dashboard.tasks')}</span>
          </div>
          {winRate !== null && (
            <div className="ml-auto flex items-center gap-1 text-xs font-medium text-emerald-600">
              <TrendingUp className="h-3.5 w-3.5" />
              {winRate}% {t('dashboard.won')}
            </div>
          )}
        </div>
        <StagePips stages={stages} contactsByStage={contactsByStage} total={total} />
      </CardContent>
    </Card>
  );
}

function ProjectCardSkeleton() {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-muted rounded-t-[var(--radius)]" />
      <CardHeader className="pt-5 pb-3">
        <div className="flex items-center gap-2">
          <Skeleton className="w-3 h-3 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 space-y-4">
        <div className="flex gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-2 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Harbour welcome band ─────────────────────────────────────────────────────

function HarbourWelcome({ loading, openTasks, activeSeqs, onPipeline, onTasks }) {
  const t = useT();
  const user = getCurrentUser();
  const first = (user?.full_name || user?.name || '').trim().split(' ')[0];
  const h = new Date().getHours();
  const greet = h < 12 ? t('dashboard.greet_morning') : h < 17 ? t('dashboard.greet_afternoon') : t('dashboard.greet_evening');
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const line = t('dashboard.harbour_line').replace('{tasks}', openTasks).replace('{campaigns}', activeSeqs);

  return (
    <section className="harbour-band" data-testid="dashboard-harbour">
      <img src="/harbour-scene.svg" alt="" aria-hidden="true" className="harbour-img" />
      <div className="harbour-shade" aria-hidden="true" />
      <div className="relative px-6 py-7 md:px-10 md:py-10 max-w-xl min-h-[230px] md:min-h-[270px] flex flex-col justify-center">
        <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '0.16em', color: '#F0B56E' }}>{today}</p>
        <h2 className="font-display text-3xl md:text-[42px] leading-[1.05] mt-2" style={{ color: '#F6F1E7' }}>
          {greet}{first ? `, ${first}` : ''}.
        </h2>
        <p className="mt-3 text-sm md:text-base max-w-md" style={{ color: '#E4DCCD' }}>
          {loading ? t('dashboard.harbour_loading') : line}
        </p>
        <div className="flex gap-2 mt-5 flex-wrap">
          <button onClick={onPipeline} className="rounded-full px-5 py-2.5 text-sm font-bold text-white transition-colors" style={{ background: '#A62B1F' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#8C2318'; }} onMouseLeave={e => { e.currentTarget.style.background = '#A62B1F'; }}>
            {t('dashboard.open_pipeline')}
          </button>
          <button onClick={onTasks} className="rounded-full px-5 py-2.5 text-sm font-bold border transition-colors hover:bg-white/10" style={{ color: '#F6F1E7', borderColor: 'rgba(246,241,231,.5)' }}>
            {t('dashboard.open_tasks_btn')}
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const { projects, loading: projectLoading, switchProject } = useProject();
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [projectPipelines, setProjectPipelines] = useState([]);
  const [seqsByProject, setSeqsByProject] = useState({});
  const [tasksByProject, setTasksByProject] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [socialCount, setSocialCount] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [pipelineRes, seqsRes, tasksRes, activitiesRes, socialRes] = await Promise.all([
        pipelineAPI.get({}).catch(() => ({ data: [] })),
        sequencesAPI.list().catch(() => ({ data: [] })),
        tasksAPI.list({}).catch(() => ({ data: [] })),
        activitiesAPI.getRecent({ limit: 6 }).catch(() => ({ data: [] })),
        socialAPI.list({ status: 'published', limit: 200 }).catch(() => ({ data: [] })),
      ]);

      setProjectPipelines(pipelineRes.data || []);

      const seqMap = {};
      for (const seq of (seqsRes.data || [])) {
        if (seq.is_active !== false) {
          seqMap[seq.project_id] = (seqMap[seq.project_id] || 0) + 1;
        }
      }
      setSeqsByProject(seqMap);

      const taskMap = {};
      for (const task of (tasksRes.data || [])) {
        if (!['done', 'completed'].includes(task.status)) {
          const pid = task.project_id || '__none__';
          taskMap[pid] = (taskMap[pid] || 0) + 1;
        }
      }
      setTasksByProject(taskMap);

      setRecentActivities(activitiesRes.data || []);

      const socialPosts = Array.isArray(socialRes.data) ? socialRes.data : [];
      setSocialCount(socialPosts.filter(p => p.status === 'published').length);
    } catch (err) {
      console.error('Dashboard load error', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!projectLoading) loadData();
  }, [projectLoading, loadData]);

  // Pipeline rows are now accounts (the deal entity) plus any standalone
  // contacts with no account yet — not a flat `contacts` array anymore.
  const dealRowsFor = (p) => [
    ...(p.accounts || []).map(a => a.account),
    ...(p.standalone_contacts || []),
  ];

  const totalContacts = projectPipelines.reduce((s, p) => s + dealRowsFor(p).length, 0);
  const totalActiveSeqs = Object.values(seqsByProject).reduce((s, v) => s + v, 0);
  const totalOpenTasks = Object.values(tasksByProject).reduce((s, v) => s + v, 0);
  const totalWon = projectPipelines.reduce((s, p) => s + dealRowsFor(p).filter(c => c.stage === 'won').length, 0);
  const totalClosed = projectPipelines.reduce((s, p) => s + dealRowsFor(p).filter(c => c.stage === 'won' || c.stage === 'lost').length, 0);
  const overallWinRate = totalClosed ? `${Math.round((totalWon / totalClosed) * 100)}%` : '—';

  const handleProjectClick = (project) => {
    switchProject(project);
    navigate('/pipeline');
  };

  return (
    <Layout title={t('dashboard.title')}>
      <div className="space-y-6">

        <HarbourWelcome
          loading={loading}
          openTasks={totalOpenTasks}
          activeSeqs={totalActiveSeqs}
          onPipeline={() => navigate('/pipeline')}
          onTasks={() => navigate('/tasks')}
        />

        {/* KPI bar — 5 cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiCard label={t('dashboard.total_contacts')}   value={loading ? '…' : totalContacts}    icon={Users}        loading={loading && !totalContacts}    color="#4C6B82" />
          <KpiCard label={t('dashboard.active_campaigns')} value={loading ? '…' : totalActiveSeqs}  icon={ToggleRight}  loading={loading && !totalActiveSeqs}  color="#A62B1F" />
          <KpiCard label={t('dashboard.open_tasks')}       value={loading ? '…' : totalOpenTasks}   icon={CheckSquare}  loading={loading && !totalOpenTasks}   color="#C7702E" />
          <KpiCard label={t('dashboard.social_published')} value={loading ? '…' : socialCount}      icon={Share2}       loading={loading}                      color="#8A6A94" />
          <KpiCard label={t('dashboard.win_rate')}         value={loading ? '…' : overallWinRate}   icon={TrendingUp}   loading={loading}                      color="#2F6B6B" />
        </div>

        {/* Projects heading */}
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-foreground">{t('dashboard.projects')}</h2>
          {!loading && (
            <span className="text-xs text-muted-foreground">
              {projectPipelines.length} {projectPipelines.length === 1 ? t('dashboard.active_project') : t('dashboard.active_projects')}
            </span>
          )}
        </div>

        {/* Project cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <ProjectCardSkeleton key={i} />)}
          </div>
        ) : projectPipelines.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">{t('dashboard.no_projects')}</p>
              <p className="text-xs text-muted-foreground">{t('dashboard.no_projects_desc')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projectPipelines.map((p) => (
              <ProjectCard
                key={p.project._id}
                project={p.project}
                contacts={dealRowsFor(p)}
                activeSeqs={seqsByProject[p.project._id] || 0}
                openTasks={tasksByProject[p.project._id] || 0}
                onClick={() => handleProjectClick(p.project)}
              />
            ))}
          </div>
        )}

        {/* Recent Activity — secondary to Projects above, so kept compact */}
        <div className="max-w-2xl">
          <h2 className="font-display text-lg text-foreground mb-2">{t('dashboard.recent_activity')}</h2>
          <Card className="shadow-none border-border/60">
            <CardContent className="pt-3 pb-1">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">{t('dashboard.no_recent_activity')}</p>
              ) : (
                <div className="divide-y divide-border">
                  {recentActivities.map((activity, idx) => {
                    const Icon = getActivityIcon(activity.type);
                    const projectEntry = projectPipelines.find(p => p.project._id === activity.project_id);
                    const projectColor = projectEntry?.project?.color || '#A62B1F';
                    const projectName = projectEntry?.project?.name;
                    const contactName = activity.contact_name;
                    const performerName = activity.performer_name;
                    const verb = activityVerb(activity.type, t);

                    return (
                      <div key={activity._id || idx} className="flex items-start gap-3 py-3">
                        <div
                          className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
                          style={{ background: `rgba(${hexToRgb(projectColor)}, 0.12)` }}
                        >
                          <Icon className="h-3.5 w-3.5" style={{ color: projectColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Primary line: verb + contact name */}
                          <p className="text-sm text-foreground">
                            <span className="font-medium">{verb}</span>
                            {contactName && (
                              <> with <span className="font-medium">{contactName}</span>
                                {activity.contact_company && (
                                  <span className="text-muted-foreground"> · {activity.contact_company}</span>
                                )}
                              </>
                            )}
                          </p>
                          {/* Subject line if meaningful and short */}
                          {activity.subject && activity.type === 'email' && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-sm">
                              "{activity.subject}"
                            </p>
                          )}
                          {/* Meta: who, which project, when */}
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {performerName && <span>{performerName}</span>}
                            {projectName && (
                              <> · <span style={{ color: projectColor }}>{projectName}</span></>
                            )}
                            {activity.occurred_at && (
                              <> · {safeFormat(activity.occurred_at, 'MMM d, h:mm a')}</>
                            )}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize flex-shrink-0">
                          {activity.type?.replace('_', ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      </div>
    </Layout>
  );
}
